import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { ChevronRightIcon, ChevronLeftIcon } from "lucide-react";
import type { Projects } from "@repo/fetch-repository/projects";
import { Link } from "@repo/i18n/routing";
// import { AnimatePresence, motion } from "motion/react";
import { useApp } from "@repo/app/useApp";
import { useTranslations } from "next-intl";
import VideoPlayer from "./video-player";

export interface ProjectsCarouselProps {
  projects: Projects[];
}

export const ProjectCarousel = observer(
  ({ projects }: ProjectsCarouselProps) => {
    const app = useApp();
    const t = useTranslations();
    const isEditor = app.rendererMode === "editor";
    const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
    const [currentProject, setCurrentProject] = useState<Projects>(projects[0]);
    // const [direction, setDirection] = useState(0);

    const getCurrentProject = useCallback((): Projects => {
      return projects[currentProjectIndex];
    }, [currentProjectIndex, projects]);

    useEffect(() => {
      setCurrentProject(getCurrentProject());
    }, [getCurrentProject]);

    const getPreviousProject = (): Projects => {
      if (currentProjectIndex === 0) {
        return projects[projects.length - 1];
      }
      return projects[currentProjectIndex - 1];
    };

    const setNextProject = (): void => {
      // setDirection(1);
      if (currentProjectIndex === projects.length - 1) {
        setCurrentProjectIndex(0);
      } else {
        setCurrentProjectIndex((prev) => prev + 1);
      }
    };

    const setPreviousProject = (): void => {
      // setDirection(-1);
      if (currentProjectIndex === 0) {
        setCurrentProjectIndex(projects.length - 1);
      } else {
        setCurrentProjectIndex((prev) => prev - 1);
      }
    };

    function renderImage(project: Projects): React.JSX.Element | null {
      const mainImage = project.main_image.data;
      // eslint-disable-next-line no-nested-ternary -- disable nested lines
      return !Array.isArray(mainImage) ? (
        mainImage.url.includes("mp4") ? (
          <div className="size-full object-cover rounded-sm z-20 relative">
            <VideoPlayer link={mainImage.url} />
          </div>
        ) : (
          <img
            alt={mainImage.alt || project.name}
            className="size-full object-cover rounded-sm z-20 relative"
            onError={(e) => {
              e.currentTarget.src = "/images/placeholder.png";
            }}
            src={mainImage.url}
          />
        )
      ) : null;
    }

    function renderImageOpacity(project: Projects): React.JSX.Element | null {
      const mainImage = project.main_image.data;
      // eslint-disable-next-line no-nested-ternary -- disable nested lines
      return !Array.isArray(mainImage) ? (
        mainImage.url.includes("mp4") ? (
          <div className="opacity-40 object-cover size-full">
            <VideoPlayer link={mainImage.url} />
          </div>
        ) : (
          <img
            alt={mainImage.alt || project.name}
            className="opacity-40 object-cover size-full"
            onError={(e) => {
              e.currentTarget.src = "/images/placeholder.png";
            }}
            src={mainImage.url}
          />
        )
      ) : null;
    }

    return (
      <div className="container flex lg:flex-row flex-col-reverse lg:pb-20 pb-10 lg:min-h-96 relative">
        <div className="absolute ltr:left-10 rtl:right-10 top-3/4 lg:flex hidden">
          <div className="size-full relative flex items-center justify-center">
            <div className="absolute rtl:rotate-90 ltr:-rotate-90 font-thin lg:text-3xl text-xl px-2 text-gray-300 before:content-[''] before:inline-block before:bg-gray-300 before:h-px before:lg:w-14 before:w-10 after:content-[''] after:inline-block after:bg-gray-300 after:h-px after:lg:w-14 after:w-10 flex items-center">
              <span className="mx-2 uppercase rtl:-mt-3 whitespace-nowrap rtl:font-doran rtl:font-medium">
                {getCurrentProject().name}
              </span>
            </div>
          </div>
        </div>

        <div className="lg:w-4/12 w-full">
          <Link
            className=" group"
            href={`projects/${getCurrentProject().id}/${getCurrentProject().name}`}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (isEditor) {
                e.preventDefault();
              }
            }}
          >
            <h2 className="group-hover:text-primary transition-colors lg:block hidden font-helvetica rtl:font-doran leading-relaced uppercase text-xl lg:text-2xl font-thin rtl:font-bold tracking-normal lg:tracking-[2.13px] lg:rtl:tracking-normal mb-10">
              {getCurrentProject().name}
            </h2>
            <div className="flex flex-col gap-1">
              {t("projectDetail.date")}:{getCurrentProject().year}
              {!Array.isArray(currentProject.customer.data) ? (
                <div>
                  {t("projectDetail.customer")}:
                  {currentProject.customer.data.name}
                </div>
              ) : null}
              {!Array.isArray(currentProject.region.data) ? (
                <div>
                  {t("projectDetail.region")}:{currentProject.region.data.name}
                </div>
              ) : null}
              {!Array.isArray(getCurrentProject().industrial_process.data) ? (
                <div>
                  {t("projectDetail.industrialProcess")}:
                  {getCurrentProject().industrial_process.data.name}
                </div>
              ) : null}
              {!Array.isArray(getCurrentProject().project_type.data) ? (
                <div>
                  {t("projectDetail.projectType")}:
                  {getCurrentProject().project_type.data.name}
                </div>
              ) : null}
              {!Array.isArray(currentProject.rectifier_technology.data) ? (
                <div>
                  {t("projectDetail.rectifierTechnology")}:
                  {currentProject.rectifier_technology.data.name}
                </div>
              ) : null}
              {t("projectDetail.progressRate")} :{" "}
              {getCurrentProject().progress_rate}%
            </div>
          </Link>
        </div>
        <div className="lg:w-8/12 w-full relative lg:px-6 flex items-center lg:mb-0 mb-10">
          <div className="lg:w-8/12 w-9/12 lg:h-[450px] h-[350px] lg:-mb-28">
            <Link
              className="text-[12.8px] md:text-[16px] lg:text-[20px] font-helvetica rtl:font-doran text-[#6a6a6a] uppercase hover:text-[#ff7700] transition-colors"
              href={`/projects/${getCurrentProject().id}/${encodeURIComponent(
                getCurrentProject().name.replace(/\s+/g, "-").toLowerCase()
              )}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                if (isEditor) {
                  e.preventDefault();
                }
              }}
            >
              {renderImage(getCurrentProject())}
            </Link>
          </div>
          <div className="w-6/12 lg:h-[330px] h-[300px] rtl:-mr-16 ltr:-ml-16 lg:-mb-28 relative">
            {renderImageOpacity(getPreviousProject())}
            <div className="flex ltr:flex-row-reverse ltr:justify-end absolute rtl:right-16 ltr:left-16 bottom-4">
              <button
                className="text-[#00000066] rounded-full outline-none border-none hover:text-primary"
                onClick={setNextProject}
                type="button"
              >
                <ChevronRightIcon className="lg:size-10 size-8" />
              </button>
              <button
                className="text-[#00000066] rounded-full outline-none border-none hover:text-primary"
                onClick={setPreviousProject}
                type="button"
              >
                <ChevronLeftIcon className="lg:size-10 size-8" />
              </button>
            </div>
          </div>
        </div>
        <h2
          className="lg:hidden block font-helvetica rtl:font-doran uppercase text-xl lg:text-3xl font-thin rtl:font-bold tracking-normal lg:tracking-[2.13px] mb-10"
          style={{
            color: "#6A6A6A",
          }}
        >
          {getCurrentProject().name}
        </h2>
      </div>
    );
  }
);
